import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, AuthResponse } from './types';
import { AuthStoreKeys } from '@/app/constants/storeEnums';
import { secureStorage } from '@/shared/utils/encryption';


// Load persisted state from encrypted localStorage
const loadInitialState = (): AuthState => {
  try {
    const persistedAuth = secureStorage.getItem<AuthState>(AuthStoreKeys.Auth);
    if (persistedAuth) {
      return persistedAuth;
    }
  } catch (error) {
    console.error('Failed to load auth state:', error);
  }
  
  return {
    user: null,
    practice: null,
    token: null,
    refreshToken: null,
    userTypeLabel: null,
    type: null,
    isAuthenticated: false,
    list: [],
    availableFacilities: null,
  };
};

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction< AuthResponse >
    ) => {
      // Always update tokens
      state.token = action.payload.access_token;
      state.refreshToken = action.payload.refresh_token;
      state.userTypeLabel = action.payload.usertypeLabel || state.userTypeLabel;
      state.type = action.payload.type || state.type;
      
      if(action.payload.list) {
        state.list = action.payload.list;
      }
      
      // Only update user/practice if utilObj is present (login response)
      // For refresh token, utilObj is undefined, so we preserve existing user/practice
      if (action.payload.utilObj) {
        const userDetails = action.payload.utilObj.userDetails || [];
        
        // Handle single vs multiple facilities
        if (userDetails.length === 1) {
          // Single facility: Store directly and mark as authenticated
          state.user = userDetails[0].userInfo;
          state.practice = userDetails[0].practiceInfo;
          state.availableFacilities = null;
          state.isAuthenticated = true; // ✅ Authenticate after complete login
        } else if (userDetails.length > 1) {
          // Multiple facilities: Don't authenticate yet, wait for facility selection
          state.availableFacilities = userDetails;
          state.user = null;
          state.practice = null;
          state.isAuthenticated = false; // ⚠️ Wait for facility selection
        }
      } else {
        // Refresh token: Keep authenticated (user/practice already set)
        state.isAuthenticated = true;
      }
      
      // ✅ Save encrypted state to localStorage
      secureStorage.setItem(AuthStoreKeys.Auth, state);
    },
    selectFacility: (state, action: PayloadAction<number>) => {
      // Select facility by index from availableFacilities
      const selectedIndex = action.payload;
      
      if (state.availableFacilities && state.availableFacilities[selectedIndex]) {
        const selected = state.availableFacilities[selectedIndex];
        state.user = selected.userInfo;
        state.practice = selected.practiceInfo;
        // Keep availableFacilities for later switching (only clear on logout)
        state.isAuthenticated = true; // ✅ Now authenticate after facility selection
        
        secureStorage.setItem(AuthStoreKeys.Auth, state);
      }
    },
    setTwoWayTextEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.practice) return;
      state.practice.twoWayTextEnabled = action.payload ? 1 : 0;
      secureStorage.setItem(AuthStoreKeys.Auth, state);
    },
    setUserProfileImage: (state, action: PayloadAction<string | null>) => {
      // Sidebar / dashboard avatar reads from state.auth.list[0].userProfileImage,
      // so we update that entry (the SELF user) when the profile image is added,
      // changed, or removed from the Profile Settings page.
      if (state.list && state.list.length > 0 && state.list[0]) {
        state.list[0].userProfileImage = action.payload;
        secureStorage.setItem(AuthStoreKeys.Auth, state);
      }
    },
    logout: (state) => {
      // Pure state mutation only — storage / React Query / PWA cache
      // teardown is owned by `clearSession` in `@/shared/utils/clearSession`,
      // which is called from every session-ending path (useLogout.onSettled,
      // handleSessionExpired, the cross-tab BroadcastChannel listener).
      // Keeping I/O out of the reducer also avoids the previous behavior of
      // `localStorage.clear()` blanket-wiping the device id and UI prefs.
      state.user = null;
      state.practice = null;
      state.list = [];
      state.token = null;
      state.refreshToken = null;
      state.userTypeLabel = null;
      state.isAuthenticated = false;
      state.availableFacilities = null;
    },
  },
});

export const { loginSuccess, logout, selectFacility, setTwoWayTextEnabled, setUserProfileImage } = authSlice.actions;
export default authSlice.reducer;
